import { Menu } from "@hellocarbo/lasagne";
import {
  ArrowLeftRight,
  BarChart2,
  BookOpen,
  Building2,
  Calculator,
  CircleHelp,
  House,
  Library,
  LineChart,
  LogOut,
  PieChart,
  Send,
  Settings,
  Star,
  User,
  Users,
} from "lucide-react";
import { use, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMatches } from "react-router";

import logo from "@/assets/logo.svg";
import { PortalContainerContext, UserContext } from "@/contexts";
import { CustomUIMatch } from "@/hooks/useBreadcrumbs";
import { TTrackingCampaign } from "@/types";

export enum NavigationMenuLinks {
  index = "index",
  collect = "collect",
  restitution = "restitution",
  communicate = "communicate",
  trajectory = "trajectory",
  simulate = "simulate",
  users = "users",
  myAccount = "myAccount",
  myCompany = "myCompany",
  switchEntity = "switchEntity",
}

interface NavigationMenuProps {
  trackingCampaigns: TTrackingCampaign[];
  handleLogOutClick: () => void;
}

const NavigationMenu = ({
  trackingCampaigns,
  handleLogOutClick,
}: NavigationMenuProps) => {
  const { container } = use(PortalContainerContext);
  const { user } = use(UserContext);
  const { t } = useTranslation(["navigation-menu", "formatter"]);
  const [currentTrackingCampaign, setCurrentTrackingCampaign] = useState<
    TTrackingCampaign | undefined
  >();
  const [activeMenuLink, setActiveMenuLink] = useState<
    NavigationMenuLinks | undefined
  >();
  const matches = useMatches() as CustomUIMatch[];

  const filteredMatches = matches
    // first get rid of any matches that don't have handle and breadcrumb
    .filter((match) => Boolean(match.handle));

  const lastMatch = filteredMatches[filteredMatches.length - 1];

  const hasAtLeastOneFinishedTrackingCampaign = trackingCampaigns.some(
    (tc) => tc.finished
  );
  const isAdmin = user?.role === "admin";
  const isPremiumUser = user?.company?.subscriptionType === "premium";
  const isFreeTrial = user?.company?.subscriptionType === "start";
  const isTrialFinished = user?.company?.isTrialFinished;
  const currentPath = window.location.pathname;

  const getDisabledTooltipContent = ({
    needsFinishedTrackingCampaign,
    premiumOnly,
    availableForFreeTrial,
  }: {
    needsFinishedTrackingCampaign: boolean;
    premiumOnly: boolean;
    availableForFreeTrial: boolean;
  }) => {
    // Specific message for features unavailable during free trial.
    if (!availableForFreeTrial) {
      return t("tooltips.freeTrial.switchToPaid");
    }

    // Message for premium-only features when user is not premium (and not on trial for this check).
    if (premiumOnly && !isFreeTrial && !isPremiumUser) {
      return t("tooltips.upgradeToPremium");
    }

    // Messages related to needing a finished tracking campaign.
    if (needsFinishedTrackingCampaign) {
      // Specific message during free trial if campaign isn't finished.
      if (isFreeTrial) {
        return t("tooltips.freeTrial.finishYourCollect");
      }
      // Message if user has finished campaigns but the current one isn't finished.
      if (hasAtLeastOneFinishedTrackingCampaign) {
        return t("tooltips.selectAValidatedCarbonFootprint");
      }
      // Default message if no campaigns are finished yet.
      return t("tooltips.validateYourCarbonFootprint");
    }
  };

  const onTrackingCampaignChange = (id: string) => {
    const selectedId = Number(id);
    const foundTrackingCampaign = trackingCampaigns.find(
      (tc) => tc.id === selectedId
    );

    // Update the state with the newly selected campaign object.
    setCurrentTrackingCampaign(foundTrackingCampaign);
    // If the user is on communicate
    if (
      currentPath.includes("/reviews/communicate") &&
      currentPath !== `/reviews/communicate/${foundTrackingCampaign?.review_id}`
    ) {
      // If the new campaign is finished, go to its communicate page.
      if (foundTrackingCampaign?.finished) {
        window.location.href = `/reviews/communicate/${foundTrackingCampaign?.review_id}`;
      } else {
        // Otherwise, redirect to the collect page.
        window.location.href = `/scores/${foundTrackingCampaign?.score_id}/categories`;
      }
    }
    // If the user is on a reviews
    else if (
      currentPath.includes("/reviews") &&
      currentPath !== `/reviews/${foundTrackingCampaign?.review_id}`
    ) {
      // If the new campaign is finished, go to its review page.
      if (foundTrackingCampaign?.finished) {
        window.location.href = `/reviews/${foundTrackingCampaign?.review_id}`;
      } else {
        // Otherwise, redirect to the home/dashboard page.
        window.location.href = `/scores/${foundTrackingCampaign?.score_id}/categories`;
      }
    }
    // If the user is on any collect page
    else if (
      (currentPath.includes("/scores") &&
        currentPath !==
          `/scores/${foundTrackingCampaign?.score_id}/categories`) ||
      currentPath.includes("/survey")
    ) {
      // Go to the main collect page
      window.location.href = `/scores/${foundTrackingCampaign?.score_id}/categories`;
    }
  };

  const onNewCarbonFootprint = () => {
    window.location.href = `/tracking/campaigns/new`;
  };

  /**
   * Effect to persist the ID of the currently selected tracking campaign
   * to local storage whenever it changes. So it's remembered when the user
   * navigates to another page or refreshes the page.
   */
  useEffect(() => {
    if (currentTrackingCampaign) {
      localStorage.setItem(
        "currentTrackingCampaign",
        `${currentTrackingCampaign?.id}`
      );
      localStorage.setItem(
        "currentScoreId",
        `${currentTrackingCampaign?.score_id}`
      );
      localStorage.setItem(
        "currentReviewId",
        `${currentTrackingCampaign?.review_id}`
      );
    }
  }, [currentTrackingCampaign]);

  /**
   * Effect to determine and set the initial or current tracking campaign based on the URL path
   * or fall back to local storage or the latest campaign.
   * This runs when the available campaigns change or the URL path changes.
   */
  useEffect(() => {
    let foundTrackingCampaign: TTrackingCampaign | undefined;

    // Prioritize finding the campaign based on IDs present in the URL.
    if (currentPath.includes("/reviews/communicate")) {
      // Extract review_id from /reviews/communicate/:review_id
      const reviewId = Number(currentPath.split("/")[3]);
      foundTrackingCampaign = trackingCampaigns.find(
        (tc) => tc.review_id === reviewId
      );
    } else if (currentPath.includes("/reviews")) {
      // Extract review_id from /reviews/:review_id
      const reviewId = Number(currentPath.split("/")[2]);
      foundTrackingCampaign = trackingCampaigns.find(
        (tc) => tc.review_id === reviewId
      );
    } else if (currentPath.includes("/scores")) {
      // Extract score_id from /scores/:score_id/...
      const scoreId = Number(currentPath.split("/")[2]);
      foundTrackingCampaign = trackingCampaigns.find(
        (tc) => tc.score_id === scoreId
      );
    }

    // If no campaign was found based on the URL...
    if (!foundTrackingCampaign) {
      // Try to retrieve the last selected campaign ID from local storage.
      const storedId = localStorage.getItem("currentTrackingCampaign");
      if (storedId) {
        foundTrackingCampaign = trackingCampaigns.find(
          (tc) => tc.id === Number(storedId)
        );
      }
      // If still not found (e.g., invalid stored ID or first visit),
      // default to the latest campaign in the provided list since they are sorted by date.
      if (!foundTrackingCampaign && trackingCampaigns.length > 0) {
        foundTrackingCampaign = trackingCampaigns[trackingCampaigns.length - 1];
      }
    }

    // Set the determined tracking campaign in the state.
    setCurrentTrackingCampaign(foundTrackingCampaign);

    // Set the active menu link in the state.
    setActiveMenuLink(
      lastMatch?.handle.activeMenuLink?.(
        lastMatch.params,
        foundTrackingCampaign?.score_id.toString()
      )
    );
  }, [trackingCampaigns, currentPath, lastMatch]);

  return (
    <Menu
      // Company logo displayed at the top of the menu
      logoUrl={logo}
      homeItem={
        isAdmin && !isFreeTrial
          ? {
              Icon: House,
              href: `/`,
              title: t("pages.home"),
              active: activeMenuLink === NavigationMenuLinks.index,
            }
          : undefined // Don't show home item for non-admins
      }
      portalContainer={container as Element}
      // Configuration for the context switcher (tracking campaign selector)
      contextSwitcher={{
        // List of available tracking campaigns
        contexts: trackingCampaigns.map((tc) => ({
          id: `${tc.id}`,
          // Display name for the campaign, formatted like "Start date - End date"
          name: `${t("formatter:date", {
            value: new Date(tc.period_start),
          })} - ${t("formatter:date", {
            value: new Date(tc.period_end),
          })}`,
          // Status indicator for the campaign
          status: {
            name:
              tc.finished || (isFreeTrial && isTrialFinished)
                ? t("trackingCampaignSwitcher.status.finished")
                : t("trackingCampaignSwitcher.status.inProgress"),
            state:
              tc.finished || (isFreeTrial && isTrialFinished)
                ? "positive"
                : "warning",
          },
        })),
        label: t("trackingCampaignSwitcher.label"),
        newContextButton:
          !isFreeTrial && isAdmin
            ? {
                label: t("trackingCampaignSwitcher.newContextButton"),
                onClick: onNewCarbonFootprint,
              }
            : undefined,
        onChange: onTrackingCampaignChange,
        selectedContextId: `${currentTrackingCampaign?.id}`,
        selectedText: t("trackingCampaignSwitcher.selected"),
      }}
      // Main navigation groups/sections
      groups={[
        // --- Group 1 ---
        [
          // Collect Page
          {
            Icon: Calculator,
            href: `/scores/${currentTrackingCampaign?.score_id}/categories`,
            title: t("pages.measure"),
            active: activeMenuLink === NavigationMenuLinks.collect,
          },
          // Review Page (Admin only)
          ...(isAdmin
            ? [
                {
                  Icon: PieChart,
                  // Link differs for free trial vs paid
                  href: isFreeTrial
                    ? "/trial_result"
                    : `/reviews/${currentTrackingCampaign?.review_id}`,
                  title: t("pages.analyse"),
                  // Disabled logic: Trial users need trial finished, others need at least one finished campaign.
                  disabled: isFreeTrial
                    ? !isTrialFinished
                    : !currentTrackingCampaign?.finished,
                  disabledTooltipContent: getDisabledTooltipContent({
                    needsFinishedTrackingCampaign: true,
                    premiumOnly: false,
                    availableForFreeTrial: true,
                  }),
                  active: activeMenuLink === NavigationMenuLinks.restitution,
                },
              ]
            : []),
          // Communicate Page (Admin only)
          ...(isAdmin
            ? [
                {
                  Icon: Send,
                  // Link differs for free trial vs paid/finished trial
                  href: isFreeTrial
                    ? "/reviews/communicate/free-trial"
                    : `/reviews/communicate/${currentTrackingCampaign?.review_id}`,
                  title: t("pages.share"),
                  // Disabled logic: Similar to Review page
                  disabled: isFreeTrial
                    ? !isTrialFinished
                    : !currentTrackingCampaign?.finished,
                  disabledTooltipContent: getDisabledTooltipContent({
                    needsFinishedTrackingCampaign: true,
                    premiumOnly: false,
                    availableForFreeTrial: true,
                  }),
                  active: activeMenuLink === NavigationMenuLinks.communicate,
                },
              ]
            : []),
        ],
        // --- Group 2 (Admin only) ---
        ...(isAdmin
          ? [
              [
                // Reduce Page
                {
                  Icon: LineChart,
                  href: `/trajectory/reduce`,
                  title: t("pages.reduce"),
                  active: activeMenuLink === NavigationMenuLinks.trajectory,
                  // Disabled logic: Trial needs completion; others need premium + at least one finished campaign.
                  disabled: isFreeTrial
                    ? !isTrialFinished
                    : !hasAtLeastOneFinishedTrackingCampaign || !isPremiumUser,
                  disabledTooltipContent: getDisabledTooltipContent({
                    needsFinishedTrackingCampaign: true,
                    premiumOnly: true,
                    availableForFreeTrial: true,
                  }),
                },
                // Simulate Page
                {
                  Icon: BarChart2,
                  href: `/simulations`,
                  title: t("pages.simulate"),
                  active: activeMenuLink === NavigationMenuLinks.simulate,
                  // Disabled logic: Trial needs completion
                  disabled: isFreeTrial && !isTrialFinished,
                  disabledTooltipContent: getDisabledTooltipContent({
                    needsFinishedTrackingCampaign: true,
                    premiumOnly: false,
                    availableForFreeTrial: false,
                  }),
                },
              ],
            ]
          : []),
      ]}
      // Optional highlight section at the bottom, shown during free trial
      highlight={
        isFreeTrial
          ? {
              title: t("freeTrialHighlight.title"),
              Icon: Star,
              buttonText: t("freeTrialHighlight.buttonText"),
              href: "/pricing",
            }
          : undefined
      }
      // Footer items
      footer={[
        // Resources Menu
        {
          Icon: BookOpen,
          portalContainer: container as Element,
          subItems: [
            {
              Icon: Library,
              href: "https://academy.hellocarbo.com/",
              shouldOpenNewTab: true,
              title: t("pages.carboAcademy"),
            },
            {
              Icon: CircleHelp,
              href: "https://hellocarbo-25361575.hs-sites-eu1.com/knowledge",
              shouldOpenNewTab: true,
              title: t("pages.helpCenter"),
            },
          ],
          title: t("pages.resources"),
          // Disabled logic: Not available during free trial.
          disabled: isFreeTrial,
          disabledTooltipContent: getDisabledTooltipContent({
            needsFinishedTrackingCampaign: false,
            premiumOnly: false,
            availableForFreeTrial: false,
          }),
        },
        // User/Account Menu
        {
          Icon: User,
          portalContainer: container as Element,
          subItems: [
            // Switch Entity (Admin only)
            ...(isAdmin && user.subsidiaries.length > 0
              ? [
                  {
                    Icon: ArrowLeftRight,
                    href: "/subsidiary-choice",
                    title: t("pages.switchEntity"),
                    active: activeMenuLink === NavigationMenuLinks.switchEntity,
                  },
                ]
              : []),
            // My Company (Admin only)
            ...(isAdmin
              ? [
                  {
                    Icon: Building2,
                    href: `/companies/${user?.company.public_id}/edit`,
                    title: t("pages.myCompany"),
                    active: activeMenuLink === NavigationMenuLinks.myCompany,
                  },
                ]
              : []),
            // My Company (Admin only)
            ...(isAdmin
              ? [
                  {
                    Icon: Users,
                    href: `/users`,
                    title: t("pages.users"),
                    active: activeMenuLink === NavigationMenuLinks.users,
                  },
                ]
              : []),
            // My Account (All users)
            {
              Icon: Settings,
              href: "/users/edit",
              title: t("pages.myAccount"),
              active: activeMenuLink === NavigationMenuLinks.myAccount,
            },
            // Sign Out (All users)
            {
              Icon: LogOut,
              onClick: handleLogOutClick,
              title: t("pages.signOut"),
            },
          ],
          subtitle: user?.company.name,
          title: t(`roles.${user?.role}`),
        },
      ]}
    />
  );
};

export default NavigationMenu;
